export class VideoRecorderManager {
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];
  private isRecording: boolean = false;

  public startRecording(stream: MediaStream): boolean {
    if (!stream || stream.getVideoTracks().length === 0) {
      console.warn('[RECORDER] No hay pista de video en el stream para grabar.');
      return false;
    }

    this.recordedChunks = [];

    // Priorizar codecs con mejor compresión en navegador
    const mimeTypes = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4'
    ];

    let selectedMime = '';
    for (const mime of mimeTypes) {
      if (MediaRecorder.isTypeSupported(mime)) {
        selectedMime = mime;
        break;
      }
    }

    try {
      this.mediaRecorder = new MediaRecorder(stream, {
        mimeType: selectedMime || undefined,
        videoBitsPerSecond: 2_200_000, // 2.2 Mbps para nitidez HD clínica y carga veloz
        audioBitsPerSecond: 64_000    // 64 kbps optimizado para canal de voz médico
      });

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };

      this.mediaRecorder.start(2000); // Guardar trozos cada 2s para evitar saturación de memoria
      this.isRecording = true;
      console.log(`[RECORDER] Grabación forense iniciada con codec: ${selectedMime || 'default'}`);
      return true;
    } catch (e) {
      console.error('[RECORDER ERROR] No se pudo inicializar MediaRecorder:', e);
      return false;
    }
  }

  public async stopAndUpload(
    apiBaseUrl: string,
    sessionId: string,
    incidentFolio: string
  ): Promise<boolean> {
    if (!this.mediaRecorder || !this.isRecording) {
      return false;
    }

    return new Promise((resolve) => {
      this.mediaRecorder!.onstop = async () => {
        this.isRecording = false;
        if (this.recordedChunks.length === 0) {
          console.warn('[RECORDER] Grabación vacía, no hay datos para subir.');
          return resolve(false);
        }

        const mimeType = this.mediaRecorder?.mimeType || 'video/webm';
        const blob = new Blob(this.recordedChunks, { type: mimeType });
        const formData = new FormData();
        const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
        formData.append('video', blob, `session_${sessionId}.${extension}`);
        formData.append('incidentFolio', incidentFolio);

        console.log(`[RECORDER] Subiendo ${Math.round(blob.size / 1024)} KB al servidor para compresión FFmpeg...`);

        try {
          const res = await fetch(`${apiBaseUrl}/api/v1/media/sessions/${sessionId}/recording`, {
            method: 'POST',
            body: formData
          });

          if (res.ok) {
            const data = await res.json();
            console.log('[RECORDER SUCCESS] Grabación subida y comprimida:', data);
            resolve(true);
          } else {
            console.error('[RECORDER UPLOAD ERROR] HTTP status:', res.status);
            resolve(false);
          }
        } catch (err) {
          console.error('[RECORDER UPLOAD EXCEPTION]', err);
          resolve(false);
        }
      };

      try {
        this.mediaRecorder!.stop();
      } catch (err) {
        console.error('[RECORDER STOP ERROR]', err);
        resolve(false);
      }
    });
  }
}
